/**
 * V Component - Inline Value Display
 *
 * Renders a value from the computation graph with:
 * - Dotted underline indicating it's interactive
 * - Click to open inspector panel
 * - Hover to show inline adjustment controls (for assumptions)
 * - Hover to show formula preview (for computed values)
 */

import React, { useRef } from 'react';
import { useGraph } from '../GraphContext';
import { useNode, usePopup } from '../hooks';
import { NodeId, isAssumptionNode, isComputedNode, isSourceNode } from '../../types';
import { InlineAdjust } from './InlineAdjust';
import { FormulaDisplay } from './FormulaDisplay';
import { Popup } from './Popup';

export interface VProps {
  // The node ID to display
  id: NodeId;
  // Format override ('currency' | 'percent' | 'number' | custom function)
  format?: 'currency' | 'percent' | 'number' | ((value: number) => string);
  // Decimal places
  decimals?: number;
  // Use compact formatting for large numbers ($1.2M instead of $1,200,000)
  compact?: boolean;
  // Additional CSS classes
  className?: string;
  // Override click behavior
  onClick?: () => void;
  // Show inline popup on hover (default true)
  showPopup?: boolean;
  // Show half ownership (divide by 2)
  halfOwnership?: boolean;
}

export function V({
  id,
  format,
  decimals,
  compact = false,
  className = '',
  onClick,
  showPopup: showPopupProp = true,
  halfOwnership = false,
}: VProps): React.ReactElement {
  const ctx = useGraph();
  const { value, node, isOverridden, inspect } = useNode(id);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLSpanElement>(null);

  // Use centralized popup hook for consistent behavior
  const popup = usePopup();

  // Determine node types
  const isAssumption = node && isAssumptionNode(node);
  const isComputed = node && isComputedNode(node);
  const isSource = node && isSourceNode(node);

  // Handle missing node
  if (value === undefined || !node) {
    return (
      <span className={`text-red-500 ${className}`} title={`Unknown node: ${id}`}>
        ???
      </span>
    );
  }

  // Apply half ownership if needed
  const displayValue = halfOwnership ? value / 2 : value;

  // Format the value
  let formattedValue: string;
  if (typeof format === 'function') {
    formattedValue = format(displayValue);
  } else {
    const unit = format || node.unit || 'number';
    formattedValue = ctx.formatValue(displayValue, unit, { decimals, compact });
  }

  // Handle click
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      inspect();
    }
  };

  // Base styles - grayscale per CLAUDE.md
  const baseStyles = `
    cursor-pointer
    border-b border-dotted border-gray-400
    hover:border-gray-600
    transition-colors
    ${isOverridden ? 'font-medium text-gray-900' : ''}
    ${className}
  `.trim();

  return (
    <span
      ref={containerRef}
      className="relative inline-block"
      {...(showPopupProp ? popup.triggerProps : {})}
    >
      <span
        ref={triggerRef}
        className={baseStyles}
        onClick={handleClick}
        title={node.label}
      >
        {formattedValue}
      </span>

      {/* Source: Show citation info */}
      {popup.isVisible && isSource && (
        <Popup isVisible {...popup.popupProps} triggerRef={containerRef}>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Source</div>
          <div className="text-sm font-medium text-gray-900 mb-1">{node.citation.source}</div>
          {node.citation.description && (
            <div className="text-xs text-gray-600 mb-2">{node.citation.description}</div>
          )}
          {node.citation.url && (
            <a
              href={node.citation.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              View source
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </Popup>
      )}

      {/* Inline adjustment controls for assumptions */}
      {popup.isVisible && isAssumption && (
        <InlineAdjust
          nodeId={id}
          node={node}
          value={value}
          onClose={popup.hide}
          onMouseEnter={popup.onPopupMouseEnter}
          onMouseLeave={popup.onPopupMouseLeave}
          triggerRef={containerRef}
        />
      )}

      {/* Formula preview for computed values */}
      {popup.isVisible && isComputed && (
        <Popup isVisible {...popup.popupProps} minWidth="min-w-72" triggerRef={containerRef}>
          <div className="text-xs text-gray-500 mb-1">{node.label}</div>
          <div className="text-sm font-medium text-gray-900 mb-2">
            {ctx.formatValue(value, node.unit)}
          </div>
          <div className="text-xs text-gray-500 mb-1">Formula</div>
          <div className="text-xs text-gray-700">
            <FormulaDisplay formula={node.formula} depth={1} maxDepth={3} />
          </div>
        </Popup>
      )}

      {/* Override indicator */}
      {isOverridden && (
        <span
          className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-gray-500 rounded-full"
          title="This value has been adjusted"
        />
      )}
    </span>
  );
}

export default V;
